import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Image as ImageIcon, ShoppingBag, ExternalLink, Building2 } from "lucide-react";
import { auth } from "../lib/api";
import Logo from "../components/Logo";
import PortfolioManager from "../components/admin/PortfolioManager";
import ProductsManager from "../components/admin/ProductsManager";
import SpaceManager from "../components/admin/SpaceManager";

const TABS = [
    { id: "portfolio", label: "Portfolio", icon: ImageIcon },
    { id: "space", label: "Espacio", icon: Building2 },
    { id: "products", label: "Productos", icon: ShoppingBag },
];

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [tab, setTab] = useState("portfolio");
    const [user, setUser] = useState(null);

    useEffect(() => {
        let mounted = true;
        if (!auth.isLogged()) {
            navigate("/admin/login", { replace: true });
            return;
        }
        auth.me()
            .then((u) => mounted && setUser(u))
            .catch(() => {
                auth.logout();
                navigate("/admin/login", { replace: true });
            });
        return () => {
            mounted = false;
        };
    }, [navigate]);

    const handleLogout = () => {
        auth.logout();
        navigate("/admin/login", { replace: true });
    };

    if (!user) return null;

    return (
        <div
            data-testid="admin-dashboard"
            className="min-h-screen bg-[#FAFAF8]"
        >
            <header className="bg-white border-b border-[#e6e2db] sticky top-0 z-20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                        <Logo size="sm" />
                        <span className="hidden sm:inline-block text-[10px] tracking-[0.32em] uppercase text-[#C4A882]">
                            Panel admin
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <a
                            href="/"
                            target="_blank"
                            rel="noreferrer"
                            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-[10px] tracking-[0.28em] uppercase text-[#6b665f] hover:text-[#1C1A18]"
                            data-testid="admin-view-site"
                        >
                            <ExternalLink size={14} /> Ver web
                        </a>
                        <button
                            type="button"
                            onClick={handleLogout}
                            data-testid="admin-logout-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 border border-[#1C1A18] text-[10px] tracking-[0.28em] uppercase text-[#1C1A18] hover:bg-[#1C1A18] hover:text-[#FAFAF8] transition-colors"
                        >
                            <LogOut size={14} /> Salir
                        </button>
                    </div>
                </div>
                <nav className="max-w-7xl mx-auto px-6 lg:px-10 flex gap-8 border-t border-[#f0ece4]">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const active = tab === t.id;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTab(t.id)}
                                data-testid={`admin-tab-${t.id}`}
                                className={`inline-flex items-center gap-2 py-4 text-[11px] tracking-[0.28em] uppercase border-b-2 transition-colors ${
                                    active
                                        ? "text-[#1C1A18] border-[#C4A882]"
                                        : "text-[#6b665f] border-transparent hover:text-[#1C1A18]"
                                }`}
                            >
                                <Icon size={14} /> {t.label}
                            </button>
                        );
                    })}
                </nav>
            </header>

            <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
                {tab === "portfolio" && <PortfolioManager />}
                {tab === "space" && <SpaceManager />}
                {tab === "products" && <ProductsManager />}
            </main>
        </div>
    );
}
